import { useState } from 'react'
import { useEndorsements, usePrograms } from '@/api/enrollment-api'
import type { EndorsementOption } from '@/api/types/enrollment'
import { useEnrollmentStore } from '@/stores'
import { getAdditionalInfoSelectionError } from './helpers'
import { getAdditionalInfoSelectionState, getNextEndorsements } from './state'
import { useAdditionalInfoTrainingModeSync } from './useAdditionalInfoTrainingModeSync'
import type { EligibilityFormData } from '../types'

type UseAdditionalInfoStepArgs = {
  formData: EligibilityFormData
  updateFormData: (data: Partial<EligibilityFormData>) => void
}

export function useAdditionalInfoStep({
  formData,
  updateFormData
}: UseAdditionalInfoStepArgs) {
  const { data: endorsements = [], isPending, isError } = useEndorsements()
  const [activeEndorsement, setActiveEndorsement] = useState<EndorsementOption | null>(null)
  const [selectionError, setSelectionError] = useState<string | null>(null)
  const storedLocationExternalId = useEnrollmentStore(state => state.formData.locationExternalId)
  const storedProgramExternalId = useEnrollmentStore(state => state.formData.programExternalId)
  const sessionToken = useEnrollmentStore(state => state.sessionToken)
  const selectedLocationExternalId = formData.location || storedLocationExternalId || null
  const { data: programs = [] } = usePrograms(selectedLocationExternalId, sessionToken)
  const {
    endorsementOptions,
    isInPersonAvailable,
    isTrainingQuestionIncomplete,
    shouldShowLaptopQuestion
  } = getAdditionalInfoSelectionState({
    endorsements,
    formData,
    programs,
    storedLocationExternalId,
    storedProgramExternalId
  })

  useAdditionalInfoTrainingModeSync({
    isInPersonAvailable,
    setSelectionError,
    shouldShowLaptopQuestion,
    trainingMode: formData.trainingMode,
    updateFormData
  })

  const toggleEndorsement = (value: string) => {
    updateFormData({ endorsements: getNextEndorsements(formData.endorsements || [], value) })
  }

  const clearSelectionError = () => {
    setSelectionError(null)
  }

  const validateTrainingQuestions = () => {
    const nextSelectionError = getAdditionalInfoSelectionError({
      shouldShowLaptopQuestion,
      trainingMode: formData.trainingMode,
      hasLaptop: formData.hasLaptop
    })

    setSelectionError(nextSelectionError)
    return nextSelectionError === null
  }

  const isEndorsementSelected = Boolean(
    activeEndorsement && formData.endorsements?.includes(activeEndorsement.type)
  )

  return {
    isPending,
    isError,
    activeEndorsement,
    setActiveEndorsement,
    selectionError,
    clearSelectionError,
    endorsementOptions,
    isInPersonAvailable,
    shouldShowLaptopQuestion,
    isTrainingQuestionIncomplete,
    toggleEndorsement,
    validateTrainingQuestions,
    isEndorsementSelected
  }
}
